import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, RollbackOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  inventoryApi,
  type StorePurchase,
  type StorePurchasePayment,
} from '@/api/inventory';
import { financeApi } from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { extractError } from '@/shared/lib/error-message';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  storeItemId: number;
  vendorId: number;
  purchaseDate: Dayjs;
  quantity: number;
  unitCost: number;
  paymentType: StorePurchasePayment;
  bankAccountId?: number;
}

export function StorePurchasesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));
  const askReason = useReasonPrompt();

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['inventory', 'store', 'purchases', params],
    queryFn: () => inventoryApi.listStorePurchases(params.from, params.to),
  });

  const { data: items } = useQuery({
    queryKey: ['inventory', 'store', 'items'],
    queryFn: inventoryApi.listStoreItems,
    staleTime: 60_000,
  });

  const { data: vendors } = useQuery({
    queryKey: ['inventory', 'vendors'],
    queryFn: inventoryApi.listVendors,
    staleTime: 60_000,
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const watchedPayment = Form.useWatch('paymentType', form);
  const quantityValue = Form.useWatch('quantity', form);
  const unitCostValue = Form.useWatch('unitCost', form);
  const totalPreview =
    quantityValue != null && unitCostValue != null
      ? Number(quantityValue) * Number(unitCostValue)
      : null;

  const add = useMutation({
    mutationFn: (v: FormShape) =>
      inventoryApi.addStorePurchase({
        storeItemId: v.storeItemId,
        vendorId: v.vendorId,
        purchaseDate: v.purchaseDate.format('YYYY-MM-DD'),
        quantity: Number(v.quantity).toFixed(2),
        unitCost: Number(v.unitCost).toFixed(2),
        paymentType: v.paymentType,
        bankAccountId: v.paymentType === 'CASH' ? v.bankAccountId : null,
      }),
    onSuccess: () => {
      message.success('Purchase recorded · stock updated · ledger posted');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      inventoryApi.reverseStorePurchase(id, reason),
    onSuccess: () => {
      message.success('Purchase reversed');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const total = (data ?? []).filter((p) => !p.reversed).reduce((s, p) => s + Number(p.totalCost), 0);

  const columns: TableProps<StorePurchase>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'purchaseDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Item',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong delete={row.reversed}>{row.storeItemName}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            from {row.vendorName}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Qty × cost',
      width: 200,
      align: 'right',
      render: (_, row) => `${PKR(row.quantity)} × PKR ${PKR(row.unitCost)}`,
    },
    {
      title: 'Total',
      dataIndex: 'totalCost',
      width: 130,
      align: 'right',
      render: (a: string, row) => (
        <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
      ),
    },
    {
      title: 'Payment',
      width: 130,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Tag color={row.paymentType === 'CASH' ? 'success' : 'warning'}>{row.paymentType}</Tag>
          {row.bankAccountName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.bankAccountName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '',
      width: 130,
      align: 'right',
      render: (_, row) =>
        row.reversed ? (
          <Tag>Reversed</Tag>
        ) : canEdit ? (
          <Button
            size="small"
            type="text"
            icon={<RollbackOutlined />}
            onClick={async () => {
              const reason = await askReason({
                title: 'Reverse purchase?',
                placeholder: 'Why is this purchase being reversed?',
                okText: 'Reverse purchase',
                danger: true,
              });
              if (reason) reverse.mutate({ id: row.id, reason });
            }}
          >
            Reverse
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.length > 0 && (
          <Tag color="processing" style={{ fontSize: 13 }}>
            Total · PKR {PKR(total)}
          </Tag>
        )}
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            form.setFieldsValue({ purchaseDate: dayjs(), paymentType: 'CASH' });
            setAdding(true);
          }}>
            Record purchase
          </Button>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No purchases in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25 }}
          />
        </Card>
      )}

      <Modal
        title="Record store purchase"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
        okText="Record"
        destroyOnHidden
        width={520}
      >
        {adding && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => add.mutate(v)}
            onValuesChange={(c) => {
              if (c.paymentType !== undefined) form.setFieldValue('bankAccountId', undefined);
            }}
          >
            <Form.Item label="Item" name="storeItemId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(items ?? []).map((i) => ({ value: i.id, label: i.name }))}
              />
            </Form.Item>
            <Form.Item label="Vendor" name="vendorId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(vendors ?? []).map((v) => ({ value: v.id, label: v.name }))}
              />
            </Form.Item>
            <Form.Item label="Purchase date" name="purchaseDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, type: 'number', min: 0.01 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} step={10} min={0} />
              </Form.Item>
              <Form.Item
                label="Unit cost (PKR)"
                name="unitCost"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} step={50} min={0} />
              </Form.Item>
            </Space.Compact>
            {totalPreview != null && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Total: PKR {PKR(totalPreview)}
              </Typography.Text>
            )}
            <Form.Item label="Payment type" name="paymentType" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'CASH', label: 'Cash / on the spot' },
                  { value: 'CREDIT', label: 'Credit (adds to vendor payable)' },
                ]}
              />
            </Form.Item>
            {watchedPayment === 'CASH' && (
              <Form.Item
                label="Pay from"
                name="bankAccountId"
                rules={[{ required: true, message: 'Required for cash purchases' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(bankAccounts ?? []).map((b) => ({
                    value: b.id,
                    label: b.accountName + (b.bankName ? ` · ${b.bankName}` : ''),
                  }))}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>
    </div>
  );
}
